import {proxy, ref} from "valtio";

import {EventEmitter} from "@/shared/event-emitter";

export type FormSubmitFeedbackEvents = {
    scrollIntoView: () => void;
};

export type FormSubmitFeedbackSetPayload = {
    /** `setError` / `setGenericError` / `setWarning`: scrolls unless `false`. `setSuccess`: scrolls only if `true`. */
    scroll?: boolean;
    title?: string;
    message?: string;
};

/** Alias of `FormSubmitFeedbackSetPayload` for `setSuccess` call sites. */
export type FormSubmitFeedbackSuccessOptions = FormSubmitFeedbackSetPayload;

/** Alias of `FormSubmitFeedbackSetPayload` (legacy name). */
export type FormSubmitFeedbackSetOptions = FormSubmitFeedbackSetPayload;

/** Inline submit feedback (success is a separate rich block in the view). */
export type FormSubmitFeedbackBanner =
    | {type: "idle"}
    | {type: "error"; title?: string; message: string}
    /** Body key defaults to `forms.submitErrorGeneric` in the view when `message` is absent. */
    | {type: "error_generic"; title?: string; message?: string}
    | {type: "warning"; title?: string; message: string}
    | {type: "success"; title?: string; message?: string};

class FormSubmitFeedbackModelClass {
    readonly events = ref(new EventEmitter<FormSubmitFeedbackEvents>()) as EventEmitter<FormSubmitFeedbackEvents>;

    banner: FormSubmitFeedbackBanner = {type: "idle"};

    scrollIntoView(): void {
        this.events.emit("scrollIntoView");
    }

    private maybeScroll(payload?: FormSubmitFeedbackSetPayload): void {
        if (payload?.scroll === false) return;
        this.scrollIntoView();
    }

    /** Empty or missing `message` after trim clears to idle. */
    setError(payload: FormSubmitFeedbackSetPayload): void {
        const text = payload.message?.trim() ?? "";
        if (!text) {
            this.clear();
            return;
        }
        const title = payload.title?.trim();
        this.banner = {type: "error", message: text, ...(title ? {title} : {})};
        this.maybeScroll(payload);
    }

    setGenericError(payload?: FormSubmitFeedbackSetPayload): void {
        const title = payload?.title?.trim();
        const message = payload?.message?.trim();
        this.banner = {
            type: "error_generic",
            ...(title ? {title} : {}),
            ...(message ? {message} : {}),
        };
        this.maybeScroll(payload);
    }

    /** Empty or missing `message` after trim clears to idle. */
    setWarning(payload: FormSubmitFeedbackSetPayload): void {
        const text = payload.message?.trim() ?? "";
        if (!text) {
            this.clear();
            return;
        }
        const title = payload.title?.trim();
        this.banner = {type: "warning", message: text, ...(title ? {title} : {})};
        this.maybeScroll(payload);
    }

    setSuccess(payload?: FormSubmitFeedbackSetPayload): void {
        const title = payload?.title?.trim();
        const message = payload?.message?.trim();
        this.banner = {
            type: "success",
            ...(title ? {title} : {}),
            ...(message ? {message} : {}),
        };
        if (payload?.scroll === true) {
            this.scrollIntoView();
        }
    }

    clear(): void {
        this.banner = {type: "idle"};
    }

    get hasError(): boolean {
        return this.banner.type === "error" || this.banner.type === "error_generic";
    }

    get isSuccess(): boolean {
        return this.banner.type === "success";
    }
}

export type FormSubmitFeedbackModelInstance = FormSubmitFeedbackModelClass;

export function FormSubmitFeedbackModel(): FormSubmitFeedbackModelInstance {
    return proxy(new FormSubmitFeedbackModelClass());
}
